import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Box, Divider, Group, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconCircleCheck, IconClock, IconDownload, IconFileText } from '@tabler/icons-react';
import UploadZone from '@/components/app/company/UploadZone';
import { useAvailableDocTypes, useDownloadDocument, useUploadSignedSpec } from '@/hooks/useDeals';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB, validateUploadFiles } from '@/lib/validateUploadFiles';
import type { BargainStepsInfo, SignatureRole } from '@/types/deals';

interface SpecSigningStepProps {
  readonly dealId: number;
  readonly info: BargainStepsInfo;
}

// Лимит размера файла — как в DocumentGroupCard (кандидат на общую константу).

// Спецификацию подписывают двое и по порядку: сначала агент, затем покупатель (модель AGENT_CPT
// на бэкенде: WAIT_AGENT_SPEC_SIGNED → WAIT_BUYER_SPEC_SIGNED). Продавец в подписи не участвует.
const ORDER: readonly SignatureRole[] = ['AGENT', 'BUYER'];

// Индекс текущей подписи: 0 агент, 1 покупатель, 2 — оба подписали.
function signingIndex(ps: string): number {
  switch (ps) {
    case 'WAIT_BUYER_SPEC_SIGNED':
      return 1;
    case 'SPECIFICATION_COMPLETED':
      return 2;
    default:
      return 0; // WAIT_AGENT_SPEC_SIGNED / BARGAIN_CREATED
  }
}

// Шаг «Подпись спецификации». Подписывается в ЭДО (Диадок) по очереди Агент→Покупатель. Экран видят
// только участники подписи (агент/покупатель) — продавцу его не показываем (см. DealStepPanel).
// Если ЭДО недоступен — фолбэк: загрузить подписанный PDF вручную (он же путь симуляции на dev).
export default function SpecSigningStep({ dealId, info }: SpecSigningStepProps) {
  const { t } = useTranslation();
  const ps = info.processingStep ?? '';
  const idx = signingIndex(ps);
  const completed = ps === 'SPECIFICATION_COMPLETED';

  const myTurn =
    (ps === 'WAIT_AGENT_SPEC_SIGNED' && info.userRole === 'AGENT') ||
    (ps === 'WAIT_BUYER_SPEC_SIGNED' && info.isBuyer === true);

  const waitingRole: SignatureRole = idx === 0 ? 'AGENT' : 'BUYER';

  // Роль зрителя среди участников подписи — определяет ролевой текст (инструкция/ожидание).
  const viewerRole: 'AGENT' | 'BUYER' | undefined =
    info.userRole === 'AGENT' ? 'AGENT' : info.isBuyer === true ? 'BUYER' : undefined;

  // Инструкция (мой ход) либо ожидание — с учётом роли зрителя.
  const statusText = myTurn
    ? viewerRole === 'AGENT'
      ? t('app.deals.spec.agentInstruction')
      : t('app.deals.spec.buyerInstruction')
    : viewerRole === 'AGENT'
      ? t('app.deals.spec.agentWaiting')
      : viewerRole === 'BUYER'
        ? t('app.deals.spec.buyerWaiting')
        : t('app.deals.spec.waiting', { role: t(`app.deals.role.${waitingRole}`) });

  const specDocs = (info.documents ?? []).filter(
    (d) => d.documentType === 'SIGNED_SPECIFICATION' || d.documentType === 'SPECIFICATION_TEMPLATE',
  );

  const download = useDownloadDocument();
  const upload = useUploadSignedSpec(dealId);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);

  // Можно ли СЕЙЧАС загрузить подписанную спецификацию — решает бэк (роль + шаг). У ЭДО-подписи
  // ручной загрузки нет (напр. на шаге агента список пуст → грузить нельзя, только Диадок).
  const availableTypes = useAvailableDocTypes(dealId);
  const canUploadSpec = (availableTypes.data ?? []).some((type) => type.documentType === 'SIGNED_SPECIFICATION');

  async function handleFiles(files: File[]): Promise<void> {
    setUploadError(undefined);
    const result = await validateUploadFiles(files, { extensions: ['pdf'], maxBytes: MAX_UPLOAD_BYTES });
    if (!result.ok) {
      setUploadError(t(result.messageKey, result.params));
      return;
    }
    upload.mutate(result.files[0]!);
  }

  return (
    <Stack gap="md">
      {/* Очередь подписи */}
      <Stack gap={6}>
        <Text fz="xs" c="gray.7">
          {t('app.deals.spec.order')}
        </Text>
        {ORDER.map((role, i) => {
          const state = i < idx ? 'done' : i === idx ? 'current' : 'pending';
          return (
            <Group key={role} gap="xs" wrap="nowrap">
              {state === 'done' ? (
                <ThemeIcon size={22} radius="xl" color="lime" variant="filled">
                  <IconCheck size={13} />
                </ThemeIcon>
              ) : state === 'current' ? (
                <ThemeIcon size={22} radius="xl" color="brand" variant="filled">
                  <Box w={7} h={7} bg="white" style={{ borderRadius: '50%' }} />
                </ThemeIcon>
              ) : (
                <ThemeIcon size={22} radius="xl" color="gray" variant="light" />
              )}
              <Text fz="sm" c={state === 'pending' ? 'gray.6' : undefined} fw={state === 'current' ? 600 : 400}>
                {t(`app.deals.role.${role}`)}
              </Text>
            </Group>
          );
        })}
      </Stack>

      {/* Документ спецификации */}
      {specDocs.length > 0 ? (
        <Stack gap="xs">
          {specDocs.map((doc) => (
            <Group key={doc.id} justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
              <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
                <ThemeIcon size={32} radius="md" variant="light" color="gray">
                  <IconFileText size={18} />
                </ThemeIcon>
                <Box style={{ minWidth: 0 }}>
                  <Text fz="sm" fw={500} lineClamp={2}>
                    {doc.name ?? t('app.deals.spec.docTitle')}
                  </Text>
                </Box>
              </Group>
              {doc.id != null ? (
                <Tooltip label={t('app.deals.docs.download')}>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={t('app.deals.docs.download')}
                    loading={download.isPending && download.variables === doc.id}
                    onClick={() => download.mutate(doc.id!)}
                  >
                    <IconDownload size={18} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
            </Group>
          ))}
        </Stack>
      ) : (
        <Text fz="sm" c="gray.6">
          {t('app.deals.spec.preparing')}
        </Text>
      )}

      <Divider />

      {/* Действие / статус */}
      {completed ? (
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size={32} radius="xl" variant="light" color="lime">
            <IconCircleCheck size={18} />
          </ThemeIcon>
          <Text fz="sm" fw={500}>
            {t('app.deals.spec.completed')}
          </Text>
        </Group>
      ) : (
        <Stack gap="sm">
          {/* Чей ход подписи в ЭДО — ролевая инструкция (Диадок) или ожидание. */}
          {myTurn ? (
            <Text fz="sm" c="gray.8">
              {statusText}
            </Text>
          ) : !canUploadSpec ? (
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon size={32} radius="xl" variant="light" color="brand">
                <IconClock size={18} />
              </ThemeIcon>
              <Text fz="sm">{statusText}</Text>
            </Group>
          ) : null}

          {/* Статус документооборота по ЭДО с покупателем — показываем только агенту (когда бэк его отдал).
              Отклонение — прямое действие агента, поэтому подаём заметно (иконка + красный). */}
          {viewerRole === 'AGENT' && info.specEdoInviteStatus != null && info.specEdoInviteStatus !== 'NONE' ? (
            info.specEdoInviteStatus === 'REJECTED' ? (
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <ThemeIcon size={22} radius="xl" variant="light" color="red">
                  <IconAlertTriangle size={13} />
                </ThemeIcon>
                <Text fz="sm" c="red.7">
                  {t('app.deals.spec.edoInviteRejected')}
                </Text>
              </Group>
            ) : (
              <Text fz="sm" c="gray.7">
                {info.specEdoInviteStatus === 'ESTABLISHED'
                  ? t('app.deals.spec.edoInviteEstablished')
                  : t('app.deals.spec.edoInviteSent')}
              </Text>
            )
          ) : null}

          {/* Ручная загрузка подписанного PDF — только если бэк её сейчас разрешает (фолбэк вне ЭДО). */}
          {canUploadSpec ? (
            <Box>
              <Text fz="sm" fw={500} mb={4}>
                {t('app.deals.spec.manualTitle')}
              </Text>
              <Text fz="xs" c="gray.6" mb="xs">
                {t('app.deals.spec.manualHint')}
              </Text>
              <UploadZone
                accept=".pdf"
                hint="PDF"
                maxSizeMb={MAX_UPLOAD_MB}
                loading={upload.isPending}
                error={uploadError}
                onFiles={handleFiles}
              />
            </Box>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}
